import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, LifeBuoy, MessageCircle, X } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./beui/Button";
import { Modal } from "./beui/Modal";
import "./SupportChatWidget.css";

type Faq = Awaited<ReturnType<typeof api.listFaq>>["faqs"][number];
const categoryOf = (faq: Faq) => faq.category?.trim() || "General";

export function SupportChatWidget() {
  const [open, setOpen] = useState(false);
  return <>
    <Button type="button" aria-label="Open FAQ help" onClick={() => setOpen(true)}
      className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] right-4 z-40 h-12 w-12 rounded-full bg-brand-red p-0 text-white shadow-lg hover:bg-brand-reddark lg:bottom-16">
      <MessageCircle size={20} />
    </Button>
    {open && <FaqHelp onClose={() => setOpen(false)} />}
  </>;
}

function FaqHelp({ onClose }: { onClose: () => void }) {
  const [faqs, setFaqs] = useState<Faq[]>([]);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading");
  const [attempt, setAttempt] = useState(0);
  const [topic, setTopic] = useState<string | null>(null);
  const [selected, setSelected] = useState<Faq | null>(null);
  const step = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;
    api.listFaq().then(response => {
      if (!active) return;
      setFaqs(response.faqs);
      setLoadState("ready");
    }).catch(() => { if (active) setLoadState("error"); });
    return () => { active = false; };
  }, [attempt]);

  useEffect(() => {
    // Choices unmount at each step; move focus to its answer/prompt rather than the page body.
    if (topic || selected) step.current?.focus({ preventScroll: true });
    step.current?.scrollIntoView({ block: "nearest" });
  }, [topic, selected]);

  const categories = [...new Set(faqs.map(categoryOf))];
  function back() {
    if (selected) setSelected(null);
    else setTopic(null);
    step.current?.focus({ preventScroll: true });
  }

  return <Modal label="FAQ help" onClose={onClose} className="support-faq-dialog">
    <header className="flex shrink-0 items-center justify-between border-b border-line px-5 py-4">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-navy-50"><LifeBuoy size={21} /></span>
        <div><h2 className="font-bold">FAQ help</h2><p className="text-xs text-mut">KontrataPH</p></div>
      </div>
      <Button type="button" variant="ghost" size="icon" className="h-11 w-11" aria-label="Close FAQ help" onClick={onClose}><X size={20} /></Button>
    </header>
    <div className="support-faq-body">
      <div className="support-faq-message">Choose a topic to find an answer from our FAQs.</div>
      {topic && <div className="support-faq-message support-faq-choice">{topic}</div>}
      {selected && <>
        <div className="support-faq-message">What would you like to know?</div>
        <div className="support-faq-message support-faq-choice">{selected.question}</div>
      </>}
      <div ref={step} tabIndex={-1} className="support-faq-step" aria-label={selected ? "FAQ answer" : topic ? "Choose a question" : "Choose a topic"}>
        {selected ? <div className="support-faq-message whitespace-pre-line">{selected.answer}</div> : <>
          {topic && <div className="support-faq-message">What would you like to know?</div>}
          {loadState === "loading" && <p role="status" className="text-sm text-mut">Loading FAQs…</p>}
          {loadState === "error" && <div role="alert"><p className="text-sm">Could not load FAQs.</p><Button type="button" variant="outline" className="mt-3" onClick={() => { setLoadState("loading"); setAttempt(value => value + 1); }}>Retry</Button></div>}
          {loadState === "ready" && !faqs.length && <p className="text-sm text-mut">No FAQs are available yet. You can contact support below.</p>}
          <div className="support-faq-options">
            {!topic ? categories.map(category => <Button type="button" key={category} variant="outline" onClick={() => setTopic(category)}>{category}<ArrowRight size={16} className="shrink-0" /></Button>)
              : faqs.filter(faq => categoryOf(faq) === topic).map(faq => <Button type="button" key={faq.id} variant="outline" onClick={() => setSelected(faq)}>{faq.question}<ArrowRight size={16} className="shrink-0" /></Button>)}
          </div>
        </>}
      </div>
    </div>
    <footer className="flex shrink-0 items-center justify-between gap-2 border-t border-line p-3">
      {topic && <Button type="button" variant="ghost" onClick={back}><ArrowLeft size={16} />Back</Button>}
      {/* A fresh navigation discards FAQ state and any existing Support draft or withdrawal prefill. */}
      <Button type="button" variant="ghost" className="ml-auto" onClick={() => window.location.assign("/support?new=1")}>Contact support<ArrowRight size={16} /></Button>
    </footer>
  </Modal>;
}
